import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { NavLinkIconButton } from "@/components/ui/nav-link-icon-button";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import {
  getAdminClassGetAdminClassFilterConfigQueryOptions,
  getAdminClassGetAdminClassFilterConfigQueryKey,
  adminClassGetAdminClassesForOrganizationList,
  adminClassGetAdminClassFilterConfig,
} from "@/orval/admin-class";
import type { AdminClassListItemDTO } from "@/orval/model";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { createFileRoute } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { CopyIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { BulkCopyClassesDialog } from "./-components/bulk-copy-classes-dialog";
import type { RowSelectionState } from "@tanstack/react-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PortalPageHeader } from "@/components/portal-page-header";

const columns: CustomColumnDef<AdminClassListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "name",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Teacher" />,
    accessorKey: "teacherName",
    sortField: "teacher.FullName",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Class Day" />,
    accessorKey: "classDayName",
    sortField: "classDay.DayOfWeek",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Semester" />,
    accessorKey: "semesterName",
    sortField: "classDay.Semester.Name",
  },
  {
    header: "Time",
    accessorFn: (row) =>
      `${format(parse(row.startTime, "HH:mm:ss", new Date(0)), "h:mm a")} - ${format(parse(row.endTime, "HH:mm:ss", new Date(0)), "h:mm a")}`,
  },
  {
    header: "Enrolled",
    id: "enrolledStudentsCount",
    accessorFn: (row) => {
      const limit = row.studentLimit != null ? row.studentLimit.toString() : "Unlimited";
      return `${row.enrolledStudentsCount} / ${limit}`;
    },
  },
];

const OrganizationAdminClassesPage = () => {
  const { organizationId } = Route.useParams();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isBulkCopyDialogOpen, setIsBulkCopyDialogOpen] = useState(false);

  const getClasses = adminClassGetAdminClassesForOrganizationList;
  const getClassesFilterConfig = adminClassGetAdminClassFilterConfig;

  // Convert row selection state to class IDs
  // Row selection keys are now the actual class IDs (due to getRowId in DataTable)
  const selectedClassIds = useMemo(() => {
    return Object.keys(rowSelection).flatMap((key) => (rowSelection[key] ? [parseInt(key)] : []));
  }, [rowSelection]);

  const handleBulkCopySuccess = () => {
    setRowSelection({});
    setIsBulkCopyDialogOpen(false);
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Classes"
        actions={
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={() => selectedClassIds.length > 0 && setIsBulkCopyDialogOpen(true)}
                    variant="outline"
                    disabled={selectedClassIds.length === 0}
                    aria-disabled={selectedClassIds.length === 0}
                  >
                    <CopyIcon className="mr-2 size-4" />
                    Bulk Copy{selectedClassIds.length > 0 ? ` (${selectedClassIds.length})` : ""}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                {selectedClassIds.length > 0
                  ? `Bulk copy ${selectedClassIds.length} classes`
                  : "Select classes to enable bulk copy"}
              </TooltipContent>
            </Tooltip>

            <NavLinkIconButton to="/organizations/$organizationId/admin/classes/new" icon={<PlusIcon />}>
              Add Class
            </NavLinkIconButton>
          </>
        }
      />

      <DataTable
        columns={columns}
        queryFn={(params, signal, filters) => getClasses(organizationId, filters ?? [], params, signal)}
        queryKeyFn={(params, filters) => ["admin-classes", organizationId, params, filters]}
        filtersQueryFn={(signal) => getClassesFilterConfig(organizationId, signal)}
        filtersQueryKeyFn={() => getAdminClassGetAdminClassFilterConfigQueryKey(organizationId)}
        rowLink={(row) => ({ from: Route.fullPath, to: row.id.toString() })}
        enableRowSelection={true}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowIdFn={(row) => row.id.toString()}
      />

      <BulkCopyClassesDialog
        isOpen={isBulkCopyDialogOpen}
        onClose={() => setIsBulkCopyDialogOpen(false)}
        organizationId={organizationId}
        selectedClassIds={selectedClassIds}
        onSuccess={handleBulkCopySuccess}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/_classes/classes")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({ searchString });

    return ensureRouteQueriesData(loaderContext, [
      getAdminClassGetAdminClassFilterConfigQueryOptions(params.organizationId),
      {
        queryKey: ["admin-classes", params.organizationId, queryOptions, filters],
        queryFn: () => adminClassGetAdminClassesForOrganizationList(params.organizationId, filters ?? [], queryOptions),
      },
    ]);
  },
  component: OrganizationAdminClassesPage,
});
